import { useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faGithub } from '@fortawesome/free-brands-svg-icons'
import {
  faArrowUpRightFromSquare,
  faLock,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons'
import projectsData from './data.json'
import './index.scss'

// ─────────────────────────────────────────────────────────────
// PROJECTS — cinematic scroll deck (split-screen film strip).
//
// The page itself is the scroller, wrapped in a Lenis-style
// momentum layer: wheel input feeds a target and the real
// scrollTop glides toward it every frame, so motion keeps
// flowing for a beat after you stop. It opens on a full-viewport
// typographic hero, then a sticky stage: the LEFT column is a
// film strip of full-bleed mockup blocks sliding through the
// projects (inner layers parallax against their block), while
// the RIGHT column is a fixed dark panel whose huge title/meta
// swap per project. Remaining builds land in the tilt grid.
//
// Small screens and prefers-reduced-motion get a plain stacked
// card layout instead — same content, native scrolling.
// ─────────────────────────────────────────────────────────────

// Tilt only for fine pointers, and never when the user prefers reduced motion
const TILT_ENABLED =
  typeof window !== 'undefined' &&
  window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
  !window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Cursor-tracked 3D tilt. Writes CSS custom props on the card:
 *   --rx/--ry  → rotation toward the cursor
 *   --mx/--my  → glare hotspot position
 */
const useTilt = (max) => {
  const ref = useRef(null)

  const onMouseMove = useCallback(
    (e) => {
      const el = ref.current
      if (!el || !TILT_ENABLED) return
      const r = el.getBoundingClientRect()
      const px = (e.clientX - r.left) / r.width
      const py = (e.clientY - r.top) / r.height
      el.style.setProperty('--rx', `${((0.5 - py) * max).toFixed(2)}deg`)
      el.style.setProperty('--ry', `${((px - 0.5) * max * 1.35).toFixed(2)}deg`)
      el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`)
      el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`)
    },
    [max]
  )

  const onMouseLeave = useCallback(() => {
    const el = ref.current
    if (!el) return
    el.style.setProperty('--rx', '0deg')
    el.style.setProperty('--ry', '0deg')
  }, [])

  return { ref, onMouseMove, onMouseLeave }
}

const PUB = process.env.PUBLIC_URL
const artSrc = (proj) => `${PUB}${proj.image}`
const uiSrc = (proj) => `${PUB}${proj.uiImage}`
// Every cover ships in two sizes: <name>.jpg (1280w) and <name>-640.jpg
const srcSetFor = (path) =>
  `${PUB}${path.replace(/\.jpg$/, '-640.jpg')} 640w, ${PUB}${path} 1280w`

const GRID_SIZES = '(max-width: 640px) 100vw, (max-width: 980px) 50vw, 30vw'

const SEG = 1.5 // scroll runway per project, in stage heights
const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v)

/* Floating browser-framed UI mockup, revealed on hover (tap on touch) */
const UiPreview = ({ proj, sizes }) =>
  proj.uiImage ? (
    <figure className="ui-preview">
      <figcaption className="ui-preview__chrome">
        <span className="ui-preview__dots">
          <i /><i /><i />
        </span>
        <span className="ui-preview__bar" />
        <span className="ui-preview__tag">UI Preview</span>
      </figcaption>
      <img
        src={uiSrc(proj)}
        srcSet={srcSetFor(proj.uiImage)}
        sizes={sizes}
        alt={`${proj.title} — interface preview`}
        loading="lazy"
        decoding="async"
      />
    </figure>
  ) : null

const CardLinks = ({ proj, compact }) => (
  <div className="card-links">
    {proj.liveUrl && (
      <a href={proj.liveUrl} target="_blank" rel="noreferrer" className="card-btn card-btn--primary" data-cursor="live">
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
        <span>Live</span>
      </a>
    )}
    {proj.githubUrl && (
      <a href={proj.githubUrl} target="_blank" rel="noreferrer" className="card-btn card-btn--ghost" data-cursor="code">
        <FontAwesomeIcon icon={faGithub} />
        <span>{compact ? 'Code' : 'View Code'}</span>
      </a>
    )}
    {proj.private && (
      <span className="badge-private" title="Source is in a private repository">
        <FontAwesomeIcon icon={faLock} />
        <span>Private Build</span>
      </span>
    )}
  </div>
)

const CardMeta = ({ proj }) => (
  <div className="card-meta">
    <span className="card-meta__code">{proj.code}</span>
    <span className="card-meta__sep">—</span>
    <span className="card-meta__domain">{proj.domain}</span>
    <span className="card-meta__year">{proj.year}</span>
  </div>
)

const CardTech = ({ tags }) => (
  <p className="card-tech">
    {tags.map((tag, i) => (
      <span key={tag} className="card-tech__item">
        {i > 0 && <span className="card-tech__sep">/</span>}
        {tag}
      </span>
    ))}
  </p>
)

/* ── Grid: vertical 3D card, art on top ── */
const GridCard = ({ proj, index }) => {
  const { ref, onMouseMove, onMouseLeave } = useTilt(10)
  const [showUi, setShowUi] = useState(false)

  return (
    <div className="cell-scene">
      <article
        ref={ref}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        className="cell-card"
      >
        <div
          className={`cell-card__art art-has-ui ${showUi ? 'art--show-ui' : ''}`}
          onClick={() => !TILT_ENABLED && setShowUi((v) => !v)}
          data-cursor="ui"
        >
          <img
            src={artSrc(proj)}
            srcSet={srcSetFor(proj.image)}
            sizes={GRID_SIZES}
            alt={`${proj.title} — cover art`}
            loading="lazy"
            decoding="async"
          />
          {proj.private && (
            <span className="art-chip">
              <FontAwesomeIcon icon={faLock} /> Private
            </span>
          )}
          <UiPreview proj={proj} sizes={GRID_SIZES} />
        </div>

        <span className="cell-card__num" aria-hidden="true">
          {String(index).padStart(2, '0')}
        </span>

        <div className="cell-card__body">
          <CardMeta proj={proj} />
          <h3 className="cell-card__title">{proj.title}</h3>
          <p className="cell-card__desc">{proj.description}</p>
          <CardTech tags={proj.tags} />
          <CardLinks proj={proj} compact />
        </div>

        <div className="card-glare" aria-hidden="true" />
      </article>
    </div>
  )
}

/* ── Right panel — remounts per active index so entrances replay ── */
const PanelInfo = ({ proj, index, total }) => {
  const href = proj.liveUrl || proj.githubUrl
  const label = proj.liveUrl ? 'Open' : proj.githubUrl ? 'Code' : 'Private'

  return (
    <div className="pj-panel__inner">
      <p className="pj-panel__count" aria-hidden="true">
        {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
      </p>

      <h2 className="pj-panel__title">
        <span className="pj-panel__title-mask">
          <i>{proj.title}</i>
        </span>
        {href ? (
          <a
            className="pj-panel__open"
            href={href}
            target="_blank"
            rel="noreferrer"
            data-cursor={label.toLowerCase()}
          >
            [{label}]
          </a>
        ) : (
          <span className="pj-panel__open pj-panel__open--static">[{label}]</span>
        )}
      </h2>

      <p className="pj-panel__meta">{proj.tags.slice(0, 3).join(', ')}</p>
      <p className="pj-panel__meta pj-panel__meta--dim">Role: {proj.domain}</p>

      <p className="pj-panel__desc">{proj.description}</p>

      <div className="pj-panel__links">
        <CardLinks proj={proj} compact />
      </div>
    </div>
  )
}

// Featured first (stable within groups), deck takes the front slice
const ordered = [...projectsData].sort(
  (a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)
)
const DECK_N = Math.min(6, ordered.length)
const deckItems = ordered.slice(0, DECK_N)
const gridItems = ordered.slice(DECK_N)

const Projects = () => {
  const pageRef = useRef(null) // the scroller
  const wrapRef = useRef(null) // tall runway
  const stageRef = useRef(null) // sticky stage
  const stripRef = useRef(null) // left film strip
  const railRef = useRef(null)
  const frameRefs = useRef([]) // arc-scrolling figure elements
  const trailRef = useRef(null) // cursor-trailing hero preview
  const trailImgRef = useRef(null)
  const trailCapRef = useRef(null)
  const crossVRef = useRef(null) // tracking reticle — vertical + horizontal
  const crossHRef = useRef(null)
  const [active, setActive] = useState(0)

  // Cinematic deck only on large screens without reduced motion
  const [cine] = useState(
    () =>
      window.matchMedia('(min-width: 981px)').matches &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )

  useEffect(() => {
    if (!cine) return
    const scroller = pageRef.current
    const wrap = wrapRef.current
    const stage = stageRef.current
    const strip = stripRef.current
    if (!scroller || !wrap || !stage || !strip) return

    const N = deckItems.length
    let raf = 0
    let pSm = 0
    let activeIdx = 0
    let stageH = 0
    let wrapTop = 0
    let span = 1
    let dead = false

    // ── Lenis-style momentum ──
    // Wheel input feeds a target; the real scrollTop eases toward it every
    // frame with a *time-based* exponential so the glide is identical on
    // 60/120Hz displays and settles smoothly over ~1s. When the wheel goes
    // idle inside the deck, the target snaps to the nearest project.
    const GLIDE_TAU = 210 // ms — bigger = slower, heavier glide
    const SCRUB_TAU = 95 // ms — strip catches up a touch quicker
    const SNAP_TAU = 260 // ms — how briskly it settles onto a project
    const SNAP_IDLE = 520 // ms of no wheel before snapping engages
    const TRAVEL_Y = 0.64 // vertical travel per project (× stage height)
    const ARC_X = 104 // px — leftward offset of the card at entry/exit
    const ROT_X = 14 // deg — 3D tilt at the arc's ends

    let targetTop = scroller.scrollTop
    let lastWritten = scroller.scrollTop
    let lastT = performance.now()
    let lastWheelT = -1e9

    const onWheel = (e) => {
      if (e.ctrlKey) return // let pinch-zoom through
      e.preventDefault()
      const dy = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY
      const max = scroller.scrollHeight - scroller.clientHeight
      targetTop = Math.max(0, Math.min(max, targetTop + dy))
      lastWheelT = performance.now()
    }

    // Scrollbar drag / keyboard / anchor jumps: adopt the new position
    const onScrollSync = () => {
      if (Math.abs(scroller.scrollTop - lastWritten) > 2) {
        targetTop = scroller.scrollTop
        lastWheelT = performance.now() // treat as active, suppress snap
      }
    }

    const measure = () => {
      stageH = scroller.clientHeight
      stage.style.height = `${stageH}px`
      wrap.style.height = `${Math.round(stageH * (1 + N * SEG))}px`
      wrapTop = wrap.offsetTop
      span = Math.max(1, wrap.offsetHeight - stageH)
    }

    const tick = (now) => {
      if (dead) return
      const dt = Math.min(64, now - lastT)
      lastT = now

      // Snap the target onto the nearest project once the wheel is idle
      if (N > 1 && now - lastWheelT > SNAP_IDLE) {
        const inDeck = targetTop > wrapTop - 4 && targetTop < wrapTop + span + 4
        if (inDeck) {
          const cur = clamp01((targetTop - wrapTop) / span) * (N - 1)
          const snapTop = wrapTop + (Math.round(cur) / (N - 1)) * span
          if (Math.abs(snapTop - targetTop) > 0.6)
            targetTop += (snapTop - targetTop) * (1 - Math.exp(-dt / SNAP_TAU))
          else targetTop = snapTop
        }
      }

      // Time-based glide toward the target
      const st = scroller.scrollTop
      const next = st + (targetTop - st) * (1 - Math.exp(-dt / GLIDE_TAU))
      if (Math.abs(targetTop - next) < 0.35) {
        if (st !== targetTop) scroller.scrollTop = targetTop
      } else {
        scroller.scrollTop = next
      }
      lastWritten = scroller.scrollTop

      // Scrub value across the deck runway
      const target = clamp01((scroller.scrollTop - wrapTop) / span)
      pSm += (target - pSm) * (1 - Math.exp(-dt / SCRUB_TAU))
      if (Math.abs(target - pSm) < 0.0004) pSm = target

      const x = pSm * (N - 1) // deck position, in projects
      const idx = Math.max(0, Math.min(N - 1, Math.round(x)))
      if (idx !== activeIdx) {
        activeIdx = idx
        setActive(idx)
      }

      // ── Circular scroll (Gemini #2) ──
      // Each figure rides a curved path instead of a straight line: it
      // enters low, its centre bulges outward along a sine arc, rotates in
      // 3D and scales up to full focus at centre, then exits high — tracing
      // a slice of a big cylinder. Progress p: 0 bottom → 0.5 centre → 1 top.
      for (let k = 0; k < N; k++) {
        const fig = frameRefs.current[k]
        if (!fig) continue
        const t = k - x // 0 = centred, + = upcoming (below), − = past (above)
        const at = Math.abs(t)
        if (at > 1.5) {
          if (fig.style.visibility !== 'hidden') {
            fig.style.visibility = 'hidden'
            fig.style.opacity = '0'
          }
          continue
        }
        fig.style.visibility = 'visible'
        const p = clamp01(0.5 - t * 0.5)
        // Left at both ends (bottom-left → top-left), centred in focus:
        // the card sweeps a rightward arc through the middle of its travel.
        const ax = (Math.sin(p * Math.PI) - 1) * ARC_X
        const ty = t * stageH * TRAVEL_Y // vertical travel
        const rot = (p - 0.5) * 2 * ROT_X // −13° bottom → 0 → +13° top
        const sc = 1 - Math.min(0.34, at * 0.34)
        const op = clamp01(1 - at * 0.82)
        fig.style.transform = `translate(calc(-50% + ${ax.toFixed(1)}px), calc(-50% + ${ty.toFixed(1)}px)) rotateX(${rot.toFixed(2)}deg) scale(${sc.toFixed(3)})`
        fig.style.opacity = op.toFixed(3)
        fig.style.zIndex = String(100 - Math.round(at * 20))
      }

      if (railRef.current)
        railRef.current.style.transform = `scaleY(${pSm.toFixed(4)})`

      raf = requestAnimationFrame(tick)
    }

    measure()
    scroller.addEventListener('wheel', onWheel, { passive: false })
    scroller.addEventListener('scroll', onScrollSync, { passive: true })
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (ro) ro.observe(scroller)
    raf = requestAnimationFrame(tick)

    return () => {
      dead = true
      cancelAnimationFrame(raf)
      scroller.removeEventListener('wheel', onWheel)
      scroller.removeEventListener('scroll', onScrollSync)
      if (ro) ro.disconnect()
    }
  }, [cine])

  // ── Flat/mobile: scroll-squeeze covers ──
  // Each card's art compresses on itself as it drifts from the viewport
  // centre (up to ~30%, container scaleY) while the photo counter-scales
  // (scaleY(1/s)) so it CROPS instead of distorting — arriving covers
  // stretch open, departing ones fold closed. A whisper of parallax rides
  // on top. Everything eased time-based so it glides at any frame rate.
  useEffect(() => {
    if (cine) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const page = pageRef.current
    if (!page) return
    const arts = Array.from(page.querySelectorAll('.cell-card__art'))
    if (!arts.length) return
    const imgs = arts.map((a) => a.querySelector(':scope > img'))
    arts.forEach((a) => a.classList.add('art-squeeze'))

    const MAX_SQ = 0.3 // fully off-screen covers lose 30% of their height
    const DEAD = 0.14 // |r| below this stays uncompressed (the focus zone)
    const RANGE = 0.5 // how much travel it takes to reach full squeeze
    const TAU = 90 // ms — easing toward the target, frame-rate independent
    const state = arts.map(() => ({ s: 1, r: 0 }))
    let raf = 0
    let dead = false
    let lastT = performance.now()

    const tick = (now) => {
      if (dead) return
      const dt = Math.min(64, now - lastT)
      lastT = now
      const ease = 1 - Math.exp(-dt / TAU)
      const vh = window.innerHeight
      const rects = arts.map((a) => a.getBoundingClientRect()) // reads first
      for (let i = 0; i < arts.length; i++) {
        const rect = rects[i]
        if (rect.bottom < -vh * 0.3 || rect.top > vh * 1.3) continue
        // r: 0 = cover centred in viewport, ±0.5 = at the edges
        const r = (rect.top + rect.height / 2 - vh / 2) / vh
        const target =
          1 - MAX_SQ * clamp01((Math.abs(r) - DEAD) / RANGE)
        const st = state[i]
        st.s += (target - st.s) * ease
        st.r += (r - st.r) * ease
        arts[i].style.transform = `scaleY(${st.s.toFixed(4)})`
        if (imgs[i])
          imgs[i].style.transform = `scaleY(${(1 / st.s).toFixed(4)}) scale(1.06) translateY(${(st.r * 3.2).toFixed(2)}%)`
      }
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => {
      dead = true
      cancelAnimationFrame(raf)
      arts.forEach((a) => {
        a.classList.remove('art-squeeze')
        a.style.transform = ''
      })
      imgs.forEach((m) => m && (m.style.transform = ''))
    }
  }, [cine])

  // ── Cursor-trailing hero preview — cycles project covers as you move ──
  useEffect(() => {
    if (!cine) return
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return
    const scroller = pageRef.current
    const trail = trailRef.current
    const img = trailImgRef.current
    const cap = trailCapRef.current
    const crossV = crossVRef.current
    const crossH = crossHRef.current
    if (!scroller || !trail || !img || !cap || !crossV || !crossH) return

    const shots = deckItems
    let mx = window.innerWidth / 2
    let my = window.innerHeight * 0.4
    let tx = mx
    let ty = my
    let travel = 0
    let idx = 0
    let moved = false
    let raf = 0
    let dead = false

    // Keep the reticle + image out of the top nav band and off the edges
    const NAV_GUARD = 172
    const clampX = (v) => Math.max(200, Math.min(window.innerWidth - 48, v))
    const clampY = (v) => Math.max(NAV_GUARD, Math.min(window.innerHeight - 120, v))

    img.src = artSrc(shots[0])
    cap.textContent = `[${shots[0].title}]`

    const onMove = (e) => {
      travel += Math.hypot(e.clientX - mx, e.clientY - my)
      mx = e.clientX
      my = e.clientY
      moved = true
      if (travel > 190) {
        travel = 0
        idx = (idx + 1) % shots.length
        img.src = artSrc(shots[idx])
        cap.textContent = `[${shots[idx].title}]`
        trail.classList.remove('is-swap')
        void trail.offsetWidth // restart the swap keyframe
        trail.classList.add('is-swap')
      }
    }

    const tick = () => {
      if (dead) return
      tx += (mx - tx) * 0.2 // snappier, tracking-like follow
      ty += (my - ty) * 0.2
      const cx = clampX(tx)
      const cy = clampY(ty)
      const vis = moved
        ? clamp01(1 - scroller.scrollTop / (scroller.clientHeight * 0.6))
        : 0
      const v = String(vis)
      trail.style.opacity = v
      trail.style.transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0) translate(-50%, -50%)`
      // Reticle lines meet at the (clamped) image centre
      crossV.style.opacity = v
      crossV.style.transform = `translate3d(${cx.toFixed(1)}px, 0, 0)`
      crossH.style.opacity = v
      crossH.style.transform = `translate3d(0, ${cy.toFixed(1)}px, 0)`
      raf = requestAnimationFrame(tick)
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    raf = requestAnimationFrame(tick)
    return () => {
      dead = true
      cancelAnimationFrame(raf)
      window.removeEventListener('pointermove', onMove)
    }
  }, [cine])

  const privateCount = projectsData.filter((p) => p.private).length

  return (
    <div className={`projects-page ${cine ? 'is-cine' : 'is-flat'}`} ref={pageRef}>
      {/* ── Hero — full-viewport typographic landing ── */}
      <header className="pj-hero">
        <p className="pj-hero__kicker">
          <span className="pj-hero__rule" />
          Selected Works · 2023 — 2026
        </p>

        <h1 className="pj-hero__title" aria-label="Things I've built">
          <span className="pj-hero__line">
            <span className="pj-hero__line-inner">Things</span>
          </span>
          <span className="pj-hero__line">
            <span className="pj-hero__line-inner">
              I've <em>built</em>
            </span>
          </span>
        </h1>

        <div className="pj-hero__foot">
          <p className="pj-hero__sub">
            From autonomous AI pipelines to real storefronts — every cover
            below is AI-generated from the project itself.
          </p>
          <div className="pj-hero__stats" aria-hidden="true">
            <span className="pj-hero__stat">
              <b>{String(projectsData.length).padStart(2, '0')}</b> Builds
            </span>
            <span className="pj-hero__stat">
              <b>{String(DECK_N).padStart(2, '0')}</b> Showcased
            </span>
            <span className="pj-hero__stat">
              <b>{String(privateCount).padStart(2, '0')}</b> Private
            </span>
          </div>
        </div>

        <p className="pj-hero__cue" aria-hidden="true">
          <FontAwesomeIcon icon={faArrowDown} />
          <span>Scroll</span>
        </p>
      </header>

      {/* Cursor-trailing preview + tracking reticle on the hero */}
      {cine && (
        <>
          <div className="pj-cross pj-cross--v" ref={crossVRef} aria-hidden="true" />
          <div className="pj-cross pj-cross--h" ref={crossHRef} aria-hidden="true" />
          <div className="pj-trail" ref={trailRef} aria-hidden="true">
            <div className="pj-trail__box">
              <img ref={trailImgRef} alt="" />
            </div>
            <span className="pj-trail__cap" ref={trailCapRef} />
          </div>
        </>
      )}

      {/* ── Deck — sticky stage: arc-scrolling media left, info panel right ── */}
      {cine ? (
        <section className="pj-deck" ref={wrapRef} aria-label="Featured projects">
          <div className="pj-stage" ref={stageRef}>
            <div className="pj-stageleft" ref={stripRef}>
              {deckItems.map((p, k) => (
                <figure
                  className="pj-fig"
                  ref={(el) => (frameRefs.current[k] = el)}
                  key={p.id}
                >
                  <img
                    src={p.uiImage ? uiSrc(p) : artSrc(p)}
                    alt={`${p.title} — interface`}
                    decoding="async"
                  />
                  {p.private && (
                    <span className="art-chip">
                      <FontAwesomeIcon icon={faLock} /> Private
                    </span>
                  )}
                </figure>
              ))}
            </div>

            <aside className="pj-panel">
              <PanelInfo
                proj={deckItems[active]}
                index={active}
                total={DECK_N}
                key={active}
              />
            </aside>

            <div className="pj-stage__rail" aria-hidden="true">
              <i ref={railRef} />
            </div>
          </div>
        </section>
      ) : (
        /* Flat fallback — every deck project as a stacked card */
        <section className="pj-flat">
          {deckItems.map((p, i) => (
            <GridCard key={p.id} proj={p} index={i + 1} />
          ))}
        </section>
      )}

      {/* ── The rest of the catalogue ── */}
      {gridItems.length > 0 && (
        <section className="pj-more">
          <div className="pj-more__head">
            <p className="pj-more__kicker">
              <span className="pj-hero__rule" />
              More Builds
            </p>
            <span className="pj-more__count">
              {String(gridItems.length).padStart(2, '0')} projects
            </span>
          </div>

          <div className="projects-grid">
            {gridItems.map((p, i) => (
              <GridCard key={p.id} proj={p} index={DECK_N + i + 1} />
            ))}
          </div>
        </section>
      )}

      {/* ── Sign-off ── */}
      <footer className="pj-cta">
        <p className="pj-cta__line">Want the code behind these?</p>
        <a
          href="https://github.com/vamsi-krish-chiguruwada"
          target="_blank"
          rel="noreferrer"
          className="card-btn card-btn--primary"
          data-cursor="github"
        >
          <FontAwesomeIcon icon={faGithub} />
          <span>GitHub</span>
        </a>
      </footer>
    </div>
  )
}

export default Projects
